/* eslint-disable no-template-curly-in-string */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Container,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import axios from "axios";
import { apiUrls } from "../../services/apiUrls";
import { LoadingAnimation } from "../molecules";

export const Bundle = () => {
  const { slug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [bundleData, setBundleData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          apiUrls.bundle.getBundle.replace("${slug}", slug),
        );
        const attrs = response?.data?.data[0]?.attributes;
        setBundleData({
          name: attrs?.name,
          description: attrs?.description,
          cover_image: attrs?.cover_image?.data?.attributes?.url,
          blogs: attrs?.blogs?.data?.map((b, i) => ({
            order: i + 1,
            slug: b?.attributes?.slug,
            title: b?.attributes?.title,
            subtitle: b?.attributes?.subtitle,
            image: b?.attributes?.image?.data?.attributes?.url,
          })),
        });
      } catch (error) {
        console.error("Failed to fetch bundle:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
  };

  return (
    <>
      {isLoading || !bundleData ? (
        <LoadingAnimation />
      ) : (
        <Container
          maxWidth="lg"
          style={{ marginTop: isMobile ? "32px" : "64px", marginBottom: "80px" }}
        >
          <Stack spacing={isMobile ? "32px" : "48px"}>
            {/* Hero Banner */}
            <Box
              style={{
                position: "relative",
                height: isMobile ? "240px" : "300px",
                backgroundImage: `url(${bundleData?.cover_image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Box
                style={{
                  position: "absolute",
                  inset: 0,
                  background: isMobile
                    ? "rgba(0,0,0,0.7)"
                    : "linear-gradient(to right, rgba(0,0,0,0.8) 55%, transparent 100%)",
                  padding: isMobile ? "16px" : "32px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Stack spacing="12px">
                  <Typography
                    style={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: isMobile ? "22px" : "32px",
                      lineHeight: 1.1,
                    }}
                  >
                    {bundleData?.name}
                  </Typography>
                  <Typography
                    style={{
                      color: "rgba(255,255,255,0.8)",
                      fontWeight: 300,
                      fontSize: isMobile ? "12px" : "16px",
                      letterSpacing: "0.05em",
                      display: "-webkit-box",
                      overflow: "hidden",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 4,
                    }}
                  >
                    {bundleData?.description}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing="12px">
                  <Button
                    variant="outlined"
                    onClick={handleShare}
                    style={{
                      color: "white",
                      borderColor: "white",
                      fontSize: "12px",
                      letterSpacing: "0.1em",
                    }}
                  >
                    SHARE
                  </Button>
                </Stack>
              </Box>
            </Box>
            {/* Numbered Article Cards */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
              {bundleData?.blogs?.map((blog) => (
                <Box
                  key={blog.slug}
                  sx={{
                    width: {
                      xs: "100%",
                      sm: "calc(50% - 8px)",
                      md: "calc(33.333% - 11px)",
                    },
                  }}
                >
                  <Card style={{ height: "100%" }}>
                    <CardActionArea
                      href={`/blog/${blog.slug}`}
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                      }}
                      onClick={() =>
                        window.scrollTo({ top: 0, behavior: "smooth" })
                      }
                    >
                      <Box
                        style={{
                          position: "relative",
                          height: "180px",
                          flexShrink: 0,
                          backgroundImage: `url(${blog.image})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      >
                        <Box
                          style={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(0deg, rgba(0,0,0,0.4) 0%, transparent 60%)",
                          }}
                        />
                        <Typography
                          style={{
                            position: "absolute",
                            top: "12px",
                            left: "12px",
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "white",
                            backgroundColor: "#03559A",
                            padding: "2px 8px",
                            borderRadius: "4px",
                          }}
                        >
                          {`#${blog.order}`}
                        </Typography>
                      </Box>
                      <Stack spacing="8px" style={{ padding: "16px", flex: 1 }}>
                        <Typography
                          style={{
                            fontWeight: 600,
                            fontSize: "16px",
                            lineHeight: 1.3,
                            display: "-webkit-box",
                            overflow: "hidden",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                          }}
                          color="primary.main"
                        >
                          {blog.title}
                        </Typography>
                        <Typography
                          style={{
                            fontSize: "14px",
                            lineHeight: 1.5,
                            display: "-webkit-box",
                            overflow: "hidden",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 3,
                          }}
                          color="text.secondary"
                        >
                          {blog.subtitle}
                        </Typography>
                      </Stack>
                    </CardActionArea>
                  </Card>
                </Box>
              ))}
            </Box>
          </Stack>
        </Container>
      )}
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};
